import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment, useContext } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell } from "components/app-table";
import pdfIcon from "assets/images/pages/leave-management/pdf-icon.svg";
import { AxiosContext } from "contexts/with-interceptor-provider";
import { formatDate } from "common/calendar";
import AppStatus from "components/app-status";

const PageCertificates = (props) => {
    const context = useContext(AxiosContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const paramsRef = useRef({ page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "" });
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
    const [loading, setLoading] = useState(true);
    const isEmptyState = useMemo(() => !loading && data.items.length === 0, [loading, data]);

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setLoading(true);
        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            response = await api.get.certificate.list(sanitizeObject(payload));
        } catch (error) {
            const wasCancelled = serveRequestErrors(error);
            if (wasCancelled) return;
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.pageable,
            }));
        }

        setLoading(false);
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);

    //prettier-ignore
    const ExpiryDateCell = useCallback(({ row }) => {
        const expiryDate = row.original?.expiryDate;
        return <AppTableCell center value={expiryDate ? formatDate(expiryDate) : "-"} />;
    }, [])

    //prettier-ignore
    const DaysLeftCell = useCallback(({ row }) => {
        const daysLeft = row.original?.daysLeft;
        return <AppTableCell center value={daysLeft ? daysLeft : "-"} />;
    }, [])

    //prettier-ignore
    const CreatedDateCell = useCallback(({ row }) => {
        const createdDate = row.original?.createdDate;
        return <AppTableCell center value={createdDate ? formatDate(createdDate) : "-"} />;
    }, [])

    //prettier-ignore
    const StatusCell = useCallback(({ row }) => {
        const status = row.original?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const AttachmentCell = useCallback(({ row }) => {
        const filePath = row.original?.filePath;
        const value = filePath ? (<a href={filePath} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attachment" /></a>) : ("-");
        return (
            <AppTableCell center value={value} />
        );
    }, []);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Course",
            accessor: "courseTitle",
            disableSortBy: false,
        },
        {
            Header: "Certificate Status",
            accessor: "status",
            disableSortBy: false,
            Cell: StatusCell,
        },
        {
            Header: "Attachment",
            accessor: "attachment",
            disableSortBy: true,
            Cell: AttachmentCell,
        },
        {
            Header: "Expiry Date",
            accessor: "expiryDate",
            disableSortBy: false,
            Cell: ExpiryDateCell,
        },
        {
            Header: "Days Left",
            accessor: "daysLeft",
            disableSortBy: true,
            Cell: DaysLeftCell,
        },
        {
            Header: "Uploaded Date",
            accessor: "createdDate",
            disableSortBy: false,
            Cell: CreatedDateCell,
        }
    ], [StatusCell, AttachmentCell, ExpiryDateCell, DaysLeftCell, CreatedDateCell]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.CERTIFICATE.LIST);
        };
    }, [context]);

    return (
        <div className="page-certificates">
            <div className="certificates">
                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton={true} />}

                {!isEmptyState && (
                    <Fragment>
                        <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                    </Fragment>
                )}
            </div>
        </div>
    );
};

export default PageCertificates;
